// src/services/auditService/auditCompanyFSCategoryService.ts
import {BASE_AUDIT_API, fetchAPI} from '@/services/api';

export interface AuditCompanyFSCategoryItem {
    id: number;
    companyId: number;
    fsId: number;
    statement: string;
    aliasEn: string;
    aliasZh: string;
    parentFsIdOverride: number | null;
    displayOrder: number;
    isVisible: number; // 0/1
    isActive: number; // 0/1
}

export interface AuditBuildCompanyFSTreeItems {

    /** Company ID */
    companyId?: number;
    /** FS Category ID from account_fs_category */
    fsId: number;
    /** FS Category level */
    level?: number;
    /** FS Category code */
    code?: string;
    /** New API: simplified name field */
    name?: string;
    /** Company-specific name (EN) */
    nameEn?: string;
    /** Company-specific alias (EN) */
    aliasEn?: string;
    /** Company-specific name (ZH) */
    nameZh?: string;
    /** Company-specific alias (ZH) */
    aliasZh?: string;
    scheduleName?: string;
    scheduleCode?: string;
    /** Statement type: BS or PL */
    statement?: string;
    /** Opening balance (DR) */
    amount?: number;
    /** New API: opening amount */
    openingAmount?: number;
    /** Opening balance (CR) */
    finalAmount?: number;
    /** Company-level parent override; NULL = use base parent_id */
    parentFsIdOverride?: number | null;
    /** Display ordering */
    displayOrder?: number;
    /** New API: adjustment array */
    adjustment?: Array<{
        refNo: string;
        adjustment: number;
        adjustByPaper: boolean;
    }>;

    children?:AuditBuildCompanyFSTreeItems[];
}

export type AuditCompanyFSCategoryPayload = Omit<AuditCompanyFSCategoryItem, 'id'> & { id?: number };

const base = `${BASE_AUDIT_API}/company-fs-category`;

export async function listByCompany(companyId: number) {
    return fetchAPI<AuditCompanyFSCategoryItem[]>(
        `${base}/listByCompany?companyId=${companyId}`, {
            method: 'GET'
        });
}

export async function queryFsIdsByCompanyId(companyId: number) {
    return fetchAPI<number[]>(
        `${base}/queryFsIdsByCompanyId?companyId=${companyId}`, {
            method: 'GET'
        });
}

/**
 * Batch replace all FS categories for a company: remove existing then insert the provided list
 */
export async function batchReplace(companyId: number, statement: string, list: AuditCompanyFSCategoryPayload[]) {
    return fetchAPI<AuditCompanyFSCategoryItem[]>(
        `${base}/batchReplace?companyId=${companyId}&&statement=${statement}`, {
            method: 'POST',
            body: JSON.stringify(list),
        }
    );
}

export async function buildCompanyFSTree(companyId: number, statement: string) {
    return fetchAPI<AuditBuildCompanyFSTreeItems[]>(
        `${base}/buildCompanyFSTree?companyId=${companyId}&&statement=${statement}`, {
            method: 'GET',
        }
    );
}

export interface UpdateAliasFromAdjustmentParams {
    companyId: number;
    annualId: number;
    statement: 'BS' | 'PL';
    fsId: number;
    code: string;
    displayName: string;
}

export interface AliasUpdateResult {
    applied?: boolean;
    fsId?: number;
    code?: string;
    statement?: string;
    aliasEn?: string | null;
    aliasZh?: string | null;
    displayName?: string | null;
    locked?: boolean;
}

/** POST /company-fs-category/updateAliasFromAdjustment */
export async function updateAliasFromAdjustment(
    params: UpdateAliasFromAdjustmentParams
): Promise<AliasUpdateResult> {
    return fetchAPI<AliasUpdateResult>(`${base}/updateAliasFromAdjustment`, {
        method: 'POST',
        body: JSON.stringify(params),
    });
}
